import React, { useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import InputFileUpload from "@/src/components/common/uploadButton";
import Iconify from "@/src/components/iconify/Iconify";
import IconCode from "@/src/lib/iconCode";
import OtpInput from "react-otp-input";
import { headerApiService } from "@/src/lib/api-services/headerApiServices";
import { useToast } from "@/src/lib/hooks/useToast";
import useAuth from "@/src/lib/hooks/useAuth";
import { useHODMutation } from "@/src/lib/hooks/useHODMutation";
import CustomButton from "@/src/lib/ui/Button";
import CustomIconButton from "@/src/lib/ui/iconButton";
import TextField from "@/src/components/custom-fields/TextField";

const UploadPrescriptionForm = ({ handleClose }) => {
    const { isLoggedIn } = useAuth();
    const { showToast, showCustomToast } = useToast();
    const [currentStep, setCurrentStep] = useState(1);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [filePreviewUrl, setFilePreviewUrl] = useState(null);
    const [mobileNumber, setMobileNumber] = useState("");
    const [otpRequestId, setOtpRequestId] = useState(null);
    const [otp, setOtp] = useState("");
    const methods = useForm();

    const { handleSubmit, setValue } = methods;

    const sentOtpMutation = useHODMutation(
        (data) => headerApiService.getOtpForUploadPrecsription(data),
        {
            onSuccess: (data) => {
                setOtpRequestId(data?.data?.data?.requestId);
                showToast(data);
                setCurrentStep(3);
            },
            onError: (error) => {
                showToast(error);
            },
        }
    );

    const uploadPrecriptionMutation = useHODMutation(
        (data) => headerApiService.uploadPrecription(data),
        {
            onSuccess: (data) => {
                showToast(data);
                handleClose();
            },
            onError: (error) => {
                showToast(error);
            },
        }
    );

    const { isLoading } = uploadPrecriptionMutation;

    // Handle file upload
    const handleFileChange = (event) => {
        const file = event.target.files[0];
        setUploadedFile(file);
        setFilePreviewUrl(URL.createObjectURL(file));
        setValue("upload", file);
    };

    // Handle file removal
    const handleFileRemove = () => {
        setUploadedFile(null);
        setFilePreviewUrl(null);
        setValue("upload", null);
    };

    // Handle preview click
    const handlePreviewClick = () => {
        if (filePreviewUrl) {
            window.open(filePreviewUrl, "_blank"); // Open the preview in a new tab
        }
    };

    // Handle mobile number change
    const handleMobileNumberChange = (e) => {
        setMobileNumber(e.target.value);
    };

    // Handle OTP submit
    const handleOtpSubmit = async () => {
        try {
            const formData = new FormData();
            formData.append("file", uploadedFile);
            !isLoggedIn && formData.append("mobileNo", mobileNumber);
            !isLoggedIn && formData.append("requestId", otpRequestId);
            !isLoggedIn && formData.append("otp", otp);

            uploadPrecriptionMutation.mutate(formData);
        } catch (error) {
            console.log("Error:", error);
        }
    };

    const onSubmit = async () => {
        if (currentStep === 1) {
            if (!uploadedFile) {
                alert("Please upload a file.");
                return;
            }
            !isLoggedIn && setCurrentStep(2);
            isLoggedIn && handleOtpSubmit();
        } else if (currentStep === 2) {
            if (!mobileNumber || mobileNumber.length !== 10) {
                alert("Please enter a valid 10-digit mobile number.");
                return;
            }

            sentOtpMutation.mutate({ mobileNo: mobileNumber });
        }
    };

    // Handle resending OTP
    const handleResendOtp = async () => {
        setOtp("");
        sentOtpMutation.mutate({ mobileNo: mobileNumber });
        showCustomToast("OTP has been resent.", "success");
    };

    // Handle editing mobile number
    const handleEditMobileNumber = () => {
        setOtp("");
        setCurrentStep(2);
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="flex flex-col gap-4">
                    {currentStep === 1 && (
                        <>
                            <InputFileUpload
                                name="upload"
                                onFileChange={handleFileChange}
                                uploadedFile={uploadedFile}
                                onFileRemove={handleFileRemove}
                                isDisabled={!!uploadedFile}
                            />
                            {uploadedFile && (
                                <div className="flex flex-row items-center gap-2">
                                    <p
                                        onClick={handlePreviewClick}
                                        style={{
                                            cursor: "pointer",
                                            textDecoration: "underline",
                                            backgroundColor: "#F4F6F8",
                                            padding: "0.5rem",
                                            textDecoration: "none",
                                            borderRadius: "0.5rem",
                                            color: "#1C252E",
                                            width: "100%",
                                        }}
                                    >
                                        {uploadedFile.name}
                                    </p>
                                    <CustomIconButton
                                        onClick={handleFileRemove}
                                    >
                                        <Iconify icon={IconCode.delete} />
                                    </CustomIconButton>
                                </div>
                            )}
                            <CustomButton
                                disabled={isLoading}
                                type="submit"
                                variant="contained"
                                color="primary"
                            >
                                Next
                            </CustomButton>
                        </>
                    )}

                    {currentStep === 2 && (
                        <>
                            <div className="flex flex-col">
                                <h5 className="font-bold md:text-2xl text-xl">
                                    Enter Mobile Number
                                </h5>

                                <h6 className="text-sm font-medium mt-1">
                                    An OTP will be sent to your mobile number.
                                </h6>
                            </div>
                            <TextField
                                placeholder="Enter 10-digit mobile number"
                                value={mobileNumber}
                                onChange={handleMobileNumberChange}
                                fullWidth
                                size="small"
                            />

                            <div className="flex md:flex-row flex-col gap-2 mt-2">
                                <CustomButton
                                    size="medium"
                                    variant="text"
                                    onClick={() => setCurrentStep(1)}
                                    className="md:w-2/4 w-full border border-solid border-gray-400 text-gray-700"
                                >
                                    Back
                                </CustomButton>
                                <CustomButton
                                    size="medium"
                                    disabled={
                                        sentOtpMutation.isLoading || isLoading
                                    }
                                    onClick={onSubmit}
                                    variant="contained"
                                    color="primary"
                                    className="md:w-2/4 w-full"
                                >
                                    Get OTP
                                </CustomButton>
                            </div>
                        </>
                    )}

                    {currentStep === 3 && (
                        <>
                            <div>
                                <h5 className="font-bold md:text-2xl text-xl">
                                    Enter OTP
                                </h5>
                                <div></div>
                                <div className="flex flex-row items-center">
                                    <h6 className="text-sm font-medium mt-1">
                                        An OTP will be sent to{" "}
                                        <span className="text-primary">
                                            {mobileNumber}
                                        </span>
                                    </h6>
                                    <CustomIconButton
                                        onClick={handleEditMobileNumber}
                                    >
                                        <Iconify icon={IconCode.edit} />
                                    </CustomIconButton>
                                </div>
                            </div>

                            <OtpInput
                                value={otp}
                                onChange={(otpValue) => {
                                    setOtp(otpValue);
                                }}
                                inputType="number"
                                numInputs={6}
                                renderInput={(props, index) => (
                                    <input {...props} key={index} />
                                )}
                                inputStyle={{
                                    width: "100%",
                                    height: "3rem",
                                    margin: "0 0.5rem",
                                    fontSize: "1.5rem",
                                    borderRadius: 8,
                                    background: "#fff",
                                    border: "1px solid #bbb",
                                    color: "#000",
                                }}
                                isInputNum={true}
                                renderSeparator={<span>-</span>}
                                pattern="[0-9]*"
                            />

                            <CustomButton
                                onClick={handleOtpSubmit}
                                variant="contained"
                                color="primary"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <Iconify icon={IconCode.loadingTwoTone} />
                                ) : (
                                    "Submit OTP"
                                )}
                            </CustomButton>
                            <div className="flex flex-row">
                                <h6 className="text-sm font-medium">
                                    Didn't receive OTP?
                                </h6>
                                <h6
                                    className="text-sm text-primary font-bold ml-1 cursor-pointer"
                                    onClick={handleResendOtp}
                                >
                                    Resend OTP
                                </h6>
                            </div>
                        </>
                    )}

                    {currentStep === 4 && (
                        <>
                            <p variant="h6" color="success.main">
                                File uploaded successfully!
                            </p>
                        </>
                    )}
                </div>
            </form>
        </FormProvider>
    );
};

export default React.memo(UploadPrescriptionForm);
